import { zodResolver } from '@hookform/resolvers/zod'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { DeepPartial, useForm, UseFormReturn } from 'react-hook-form'

import {
  OtoIndividualTripStep,
  OtoIndividualTripStepInput,
  OtoIndividualTripStepsFormValues,
  UpdateOtoIndividualTripStepsInput
} from '../../../../lib/types/models/enrollment/oto-individual-trip-steps'
import { Time } from '../../../../lib/types/utils'
import {
  otoIndividualTripStepsDraftSchema,
  otoIndividualTripStepsFormSchema
} from '../../../../lib/utils/validations/oto-individual-trip-steps.validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../../elements/inputs/controlled-hour-input'
import ControlledInput from '../../../elements/inputs/controlled-input'

// The voyage individuel is one form holding both legs: the backend checks the aller against the
// retour, so they are recorded and sent together.
type LegName = keyof OtoIndividualTripStepsFormValues

// The API sends a leg with its ids and timestamps, and a leg saved as a draft sends nulls for
// what the parents had not typed yet; the form only carries what they fill.
export const mapOtoIndividualTripStepToInput = (
  tripStep?: OtoIndividualTripStep
): DeepPartial<OtoIndividualTripStepInput> | undefined => {
  if (!tripStep) return undefined

  return {
    departureDate: tripStep.departureDate,
    departurePlace: tripStep.departurePlace,
    departureTime: tripStep.departureTime,
    arrivesNextDay: tripStep.arrivesNextDay,
    arrivalPlace: tripStep.arrivalPlace,
    arrivalTime: tripStep.arrivalTime,
    transportNumber: tripStep.transportNumber,
    company: tripStep.company
  }
}

// A leg goes out exactly as it was typed: the backend replaces the stored leg with what it
// receives, so a cleared input is left out and the saved value is erased with it.
// A leg with nothing left in it is deleted on the backend side.
const toTripStepPayload = (leg?: OtoIndividualTripStepInput): OtoIndividualTripStepInput => ({
  departureDate: leg?.departureDate?.trim() || undefined,
  departurePlace: leg?.departurePlace?.trim() || undefined,
  departureTime: leg?.departureTime,
  arrivesNextDay: leg?.arrivesNextDay ?? false,
  arrivalPlace: leg?.arrivalPlace?.trim() || undefined,
  arrivalTime: leg?.arrivalTime,
  transportNumber: leg?.transportNumber?.trim() || undefined,
  company: leg?.company?.trim() || undefined
})

type TripLegFieldsProps = {
  form: UseFormReturn<OtoIndividualTripStepsFormValues>
  name: LegName
  title: string
  variant: 'warning' | 'success'
  disabled?: boolean
}

const TripLegFields = ({ form, name, title, variant, disabled }: TripLegFieldsProps) => {
  const arrivesNextDay = form.watch(`${name}.arrivesNextDay`)

  return (
    <Box className="rounded-lg border border-grey-light flex flex-col gap-4 bg-white p-4">
      <Badge text={title} variant={variant} size="medium" />

      <div className="grid grid-cols-3 gap-4 mobile:grid-cols-1 tab:grid-cols-1">
        <FormField
          control={form.control}
          name={`${name}.departureDate`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledDateStringInput
                  {...field}
                  disabled={disabled}
                  label={'Date de départ'}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`${name}.departurePlace`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  {...field}
                  type={'text'}
                  disabled={disabled}
                  label={'Lieu de départ'}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`${name}.departureTime`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledHourInput
                  {...field}
                  disabled={disabled}
                  label={'Heure de départ'}
                  value={field.value as Time | null}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name={`${name}.arrivesNextDay`}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Checkbox
                {...field}
                disabled={disabled}
                checked={!!arrivesNextDay}
                label={'Arrivée le lendemain (J+1)'}
                onChange={(checked) =>
                  form.setValue(`${name}.arrivesNextDay`, checked, { shouldValidate: true })
                }
              />
            </FormControl>
          </FormItem>
        )}
      />

      <div className="grid grid-cols-3 gap-4 mobile:grid-cols-1 tab:grid-cols-1">
        <FormField
          control={form.control}
          name={`${name}.arrivalPlace`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  {...field}
                  type={'text'}
                  disabled={disabled}
                  label={"Lieu d'arrivée"}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`${name}.arrivalTime`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledHourInput
                  {...field}
                  disabled={disabled}
                  label={"Heure d'arrivée"}
                  value={field.value as Time | null}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-3 gap-4 mobile:grid-cols-1 tab:grid-cols-1">
        <FormField
          control={form.control}
          name={`${name}.transportNumber`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  {...field}
                  type={'text'}
                  disabled={disabled}
                  label={'N° de vol/train'}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`${name}.company`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  {...field}
                  type={'text'}
                  disabled={disabled}
                  label={'Compagnie'}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </Box>
  )
}

type Props = {
  defaultValues?: DeepPartial<OtoIndividualTripStepsFormValues>
  disabled?: boolean
  isSaving?: boolean
  onSave: (input: Omit<UpdateOtoIndividualTripStepsInput, 'submit'> & { submit: boolean }) => void
}

const ClientOtoIndividualTripStepsForm = ({
  defaultValues,
  disabled,
  isSaving,
  onSave
}: Props) => {
  const form = useForm<OtoIndividualTripStepsFormValues>({
    resolver: zodResolver(otoIndividualTripStepsFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  // "Enregistrer et continuer plus tard" is a draft: whatever the parents typed is saved as it
  // is, half a leg included, and nothing is required of them yet.
  const handleSaveDraft = () => {
    const values = form.getValues()

    form.clearErrors()
    const draft = otoIndividualTripStepsDraftSchema.safeParse(values)
    if (!draft.success) {
      for (const issue of draft.error.issues)
        form.setError(issue.path.join('.') as Parameters<typeof form.setError>[0], {
          type: 'custom',
          message: issue.message
        })
      return
    }

    onSave({
      departureTripStep: toTripStepPayload(values.departureTripStep),
      returnTripStep: toTripStepPayload(values.returnTripStep),
      submit: false
    })
  }

  // "Valider et envoyer à LEC" goes through the schema first: both legs complete, the departure
  // before the arrival inside each leg, and the retour after the aller.
  const handleSubmitToLec = form.handleSubmit((values) =>
    onSave({
      departureTripStep: toTripStepPayload(values.departureTripStep),
      returnTripStep: toTripStepPayload(values.returnTripStep),
      submit: true
    })
  )

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          handleSubmitToLec()
        }}
      >
        <TripLegFields
          form={form}
          name={'departureTripStep'}
          title={'Aller simple'}
          variant={'warning'}
          disabled={disabled}
        />

        <TripLegFields
          form={form}
          name={'returnTripStep'}
          title={'Retour'}
          variant={'success'}
          disabled={disabled}
        />

        {disabled ? null : (
          <div className="flex justify-end gap-3 mobile:flex-col">
            <Button
              type={'button'}
              variant={'ghost'}
              label={'Enregistrer et continuer plus tard'}
              disabled={isSaving}
              className="mobile:w-full"
              onClick={handleSaveDraft}
            />
            <Button
              type={'submit'}
              variant={'primary'}
              label={'Valider et envoyer à LEC'}
              loading={isSaving}
              disabled={isSaving}
              className="mobile:w-full"
            />
          </div>
        )}
      </form>
    </Form>
  )
}

export default ClientOtoIndividualTripStepsForm
